import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { naytiveAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './NaytiveApplications.css';

/**
 * Compliance call queue. Lists user-submitted "Schedule a call"
 * requests from the v3 compliance calendar drawer. These DO NOT go
 * through the voice-agent pipeline — the team works the queue
 * manually:
 *   - Filter by status (pending / contacted / completed / cancelled)
 *   - Mark a row as contacted / completed / cancelled
 *   - Append timestamped admin notes (appended, not replaced, so the
 *     blob reads like a call log)
 *
 * Read endpoint:  GET   /api/naytive/compliance-call-requests
 * Mutate:         PATCH /api/naytive/compliance-call-requests/:id
 */

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatPreferredSlot = (date, time, tz) => {
  if (!date && !time) return '—';
  const parts = [];
  if (date) parts.push(new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
  if (time) {
    // The Call-me-now path sends 'now' as a sentinel; render it
    // explicitly so the admin sees "Call me now" intent rather than a
    // confusing literal string.
    parts.push(time === 'now' ? 'Call me now' : time);
  }
  return parts.join(' · ') + (tz ? `  (${tz})` : '');
};

const STATUS_OPTIONS = ['pending', 'contacted', 'completed', 'cancelled'];

const STATUS_BADGE = {
  pending:   { label: 'Pending',   bg: '#FEF3C7', fg: '#92400E' },
  contacted: { label: 'Contacted', bg: '#DBEAFE', fg: '#1E40AF' },
  completed: { label: 'Completed', bg: '#DCFCE7', fg: '#166534' },
  cancelled: { label: 'Cancelled', bg: '#F3F4F6', fg: '#6B7280' },
};

const NaytiveCompliance = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState(null);
  /** Per-row admin note draft. Keyed by request id so multiple rows
   *  can be edited in parallel without losing state. */
  const [noteDrafts, setNoteDrafts] = useState({});
  /** Track which rows are currently saving so the buttons disable. */
  const [savingId, setSavingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await naytiveAPI.listComplianceCallRequests(
        statusFilter ? { status: statusFilter } : {},
      );
      setRequests(res.data?.requests || []);
    } catch (err) {
      console.error('[NaytiveCompliance] fetch failed:', err);
      setToast({ type: 'error', text: 'Failed to load compliance calls' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const updateRow = async (id, patch) => {
    setSavingId(id);
    try {
      await naytiveAPI.updateComplianceCallRequest(id, patch);
      setToast({ type: 'success', text: 'Updated' });
      // Clear the note draft on save so the textarea resets.
      if (patch.adminNotes !== undefined) {
        setNoteDrafts((prev) => ({ ...prev, [id]: '' }));
      }
      await fetchRequests();
    } catch (err) {
      console.error('[NaytiveCompliance] update failed:', err);
      setToast({
        type: 'error',
        text: err.response?.data?.message || 'Update failed',
      });
    } finally {
      setSavingId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { all: requests.length, pending: 0, contacted: 0, completed: 0, cancelled: 0 };
    for (const r of requests) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [requests]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">COMPLIANCE CALLS</h1>
            <p className="page-subtitle">
              GET-HELP-WITH-FILING SUBMISSIONS · VOICE AGENT DIALS · TEAM FOLLOW-UP
            </p>
          </div>
          <div className="header-right">
            <button
              className="btn-secondary"
              onClick={fetchRequests}
              disabled={loading}
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
              </svg>
              REFRESH
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Status counts double as filter — clicking toggles the
              status filter, matching how NaytiveApplications uses
              stat-cards. */}
          <div
            className="stats-grid"
            style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}
          >
            <div
              className={`stat-card stat-card-clickable ${statusFilter === '' ? 'stat-card-active' : ''}`}
              onClick={() => setStatusFilter('')}
            >
              <div className="stat-info">
                <span className="stat-label">ALL</span>
                <span className="stat-value">{counts.all}</span>
              </div>
            </div>
            {STATUS_OPTIONS.map((s) => (
              <div
                key={s}
                className={`stat-card stat-card-clickable ${statusFilter === s ? 'stat-card-active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                <div className="stat-info">
                  <span className="stat-label">{STATUS_BADGE[s].label.toUpperCase()}</span>
                  <span className="stat-value">{counts[s] ?? 0}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="dashboard-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading compliance calls…</p>
              </div>
            ) : requests.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <p>No compliance calls {statusFilter ? `with status "${statusFilter}"` : ''} yet.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Filing</th>
                      <th>Preferred slot</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th style={{ minWidth: 320 }}>Notes / Actions</th>
                    </tr>
                  </thead>
              <tbody>
                {requests.map((r) => {
                  const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                  const isSaving = savingId === r.id;
                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{r.userName}</div>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>
                          {r.userEmail || '—'}
                        </div>
                        {(r.jobTitle || r.company) && (
                          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                            {[r.jobTitle, r.company].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {r.filingLabel || r.filingId || 'General compliance'}
                        </div>
                        {r.filingId && r.filingLabel && (
                          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                            id: {r.filingId}
                          </div>
                        )}
                      </td>
                      <td>{formatPreferredSlot(r.preferredDate, r.preferredTime, r.userTimezone)}</td>
                      <td style={{ fontFamily: 'monospace' }}>{r.phone || '—'}</td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: 999,
                            background: badge.bg,
                            color: badge.fg,
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                          }}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#6B7280' }}>
                        {formatDateTime(r.createdAt)}
                      </td>
                      <td>
                        {/* Existing notes (read-only log). preserve newlines so the
                            appended "[timestamp · actor]" entries read like a history. */}
                        {r.notes && (
                          <pre
                            style={{
                              fontSize: 11.5,
                              color: '#374151',
                              background: '#F9FAFB',
                              border: '1px solid #E5E7EB',
                              borderRadius: 6,
                              padding: '6px 8px',
                              margin: '0 0 8px',
                              maxHeight: 110,
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              fontFamily: 'inherit',
                            }}
                          >
                            {r.notes}
                          </pre>
                        )}

                        {/* New note + save action */}
                        <textarea
                          value={noteDrafts[r.id] ?? ''}
                          onChange={(e) =>
                            setNoteDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))
                          }
                          placeholder="Add internal note (visible to admins only)"
                          rows={2}
                          style={{
                            width: '100%',
                            fontSize: 12,
                            padding: '6px 8px',
                            border: '1px solid #D1D5DB',
                            borderRadius: 6,
                            resize: 'vertical',
                            fontFamily: 'inherit',
                          }}
                        />

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                          {/* Status action buttons — each fires a single PATCH. */}
                          {r.status !== 'contacted' && (
                            <button
                              className="btn-secondary btn-sm"
                              disabled={isSaving}
                              onClick={() =>
                                updateRow(r.id, {
                                  status: 'contacted',
                                  adminNotes: (noteDrafts[r.id] || '').trim() || undefined,
                                })
                              }
                            >
                              Mark contacted
                            </button>
                          )}
                          {r.status !== 'completed' && (
                            <button
                              className="btn-primary btn-sm"
                              disabled={isSaving}
                              onClick={() =>
                                updateRow(r.id, {
                                  status: 'completed',
                                  adminNotes: (noteDrafts[r.id] || '').trim() || undefined,
                                })
                              }
                            >
                              Mark completed
                            </button>
                          )}
                          {r.status !== 'cancelled' && (
                            <button
                              className="btn-secondary btn-sm"
                              disabled={isSaving}
                              onClick={() => {
                                if (!window.confirm('Cancel this compliance call request?')) return;
                                updateRow(r.id, {
                                  status: 'cancelled',
                                  adminNotes: (noteDrafts[r.id] || '').trim() || undefined,
                                });
                              }}
                            >
                              Cancel
                            </button>
                          )}
                          {/* "Note only" path — append a note without touching status. */}
                          {(noteDrafts[r.id] || '').trim().length > 0 && (
                            <button
                              className="btn-secondary btn-sm"
                              disabled={isSaving}
                              onClick={() =>
                                updateRow(r.id, {
                                  adminNotes: (noteDrafts[r.id] || '').trim(),
                                })
                              }
                            >
                              Save note
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ComplianceLeadsSection onToast={setToast} />
        </div>

        {toast && (
          <div className={`naytive-toast ${toast.type === 'error' ? 'toast-error' : 'toast-success'}`}>
            {toast.text}
          </div>
        )}
      </main>
    </div>
  );
};

// ───────────────────────── Professional-help leads ─────────────────────────
// Warm leads the compliance voice agent (Gemini Live) flagged during a
// "Get help with this filing" call — the founder would benefit from a
// specialist handling the filing. Shown here so ops can qualify + follow up.
const LEAD_STATUSES = ['new', 'contacted', 'converted', 'closed'];
const LEAD_STATUS_BADGE = {
  new:       { label: 'New',       bg: '#FEF9C3', fg: '#854D0E' },
  contacted: { label: 'Contacted', bg: '#DBEAFE', fg: '#1E40AF' },
  converted: { label: 'Converted', bg: '#DCFCE7', fg: '#166534' },
  closed:    { label: 'Closed',    bg: '#F3F4F6', fg: '#6B7280' },
};

function ComplianceLeadsSection({ onToast }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await naytiveAPI.listComplianceLeads();
      setLeads(res.data?.leads || []);
    } catch (err) {
      console.error('[ComplianceLeads] fetch failed:', err);
      onToast?.({ type: 'error', text: 'Failed to load leads' });
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const setStatus = async (id, status) => {
    setSavingId(id);
    try {
      await naytiveAPI.updateComplianceLead(id, { status });
      onToast?.({ type: 'success', text: 'Lead updated' });
      await fetchLeads();
    } catch (err) {
      console.error('[ComplianceLeads] update failed:', err);
      onToast?.({ type: 'error', text: err.response?.data?.message || 'Update failed' });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="dashboard-section" style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h2 className="page-title" style={{ fontSize: 18 }}>PROFESSIONAL-HELP LEADS</h2>
          <p className="page-subtitle" style={{ margin: 0 }}>
            FLAGGED BY THE FILING-HELP VOICE AGENT · WARM LEADS TO FOLLOW UP
          </p>
        </div>
        <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>{leads.length} total</span>
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner"></div></div>
      ) : leads.length === 0 ? (
        <div style={{ padding: 24, color: '#6B7280', fontSize: 14 }}>
          No leads yet — they appear when the agent flags a founder who wants hands-on help.
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Founder</th>
                <th>Company</th>
                <th>Filing</th>
                <th>Why</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <React.Fragment key={l.id}>
                  <tr>
                    <td>
                      <div style={{ fontWeight: 600 }}>{l.userName}</div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>{l.userEmail}</div>
                    </td>
                    <td>{l.entityName || '—'}</td>
                    <td>{l.filingLabel || l.filingId || '—'}</td>
                    <td style={{ maxWidth: 280, fontSize: 13 }}>{l.reason || l.callSummary || '—'}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                        background: (LEAD_STATUS_BADGE[l.status] || {}).bg, color: (LEAD_STATUS_BADGE[l.status] || {}).fg,
                      }}>
                        {(LEAD_STATUS_BADGE[l.status] || {}).label || l.status}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {(l.transcript || l.callSummary) && (
                        <button className="btn-secondary" style={{ marginRight: 6 }}
                          onClick={() => setOpenId(openId === l.id ? null : l.id)}>
                          {openId === l.id ? 'Hide' : 'Transcript'}
                        </button>
                      )}
                      <select
                        value={l.status}
                        disabled={savingId === l.id}
                        onChange={(e) => setStatus(l.id, e.target.value)}
                      >
                        {LEAD_STATUSES.map((s) => (
                          <option key={s} value={s}>{(LEAD_STATUS_BADGE[s] || {}).label || s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  {openId === l.id && (
                    <tr>
                      <td colSpan={6} style={{ background: '#F9FAFB' }}>
                        <div style={{ padding: '10px 14px' }}>
                          {l.callSummary && (
                            <div style={{ fontSize: 13, marginBottom: 8 }}>
                              <strong>Agent summary:</strong> {l.callSummary}
                            </div>
                          )}
                          {Array.isArray(l.transcript) && l.transcript.length > 0 ? (
                            <div style={{ maxHeight: 260, overflowY: 'auto', fontSize: 13, lineHeight: 1.5 }}>
                              {l.transcript.map((t, i) => (
                                <div key={i} style={{ marginBottom: 4 }}>
                                  <span style={{ fontWeight: 700, color: t.role === 'assistant' ? '#7C3AED' : '#111827' }}>
                                    {t.role === 'assistant' ? 'Kim' : 'Founder'}:
                                  </span>{' '}
                                  {t.text}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: '#6B7280' }}>No transcript captured for this call.</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default NaytiveCompliance;
